import React from 'react';
import ReviewFindingsList, { FINDING_STYLES } from './ReviewFindingsList';
import { ReviewStep, ReviewStepOutcome } from './reviewSteps';

/**
 * The reviewer's cascade for one candidate, one row per judge call in the order
 * they ran — the record the newest-verdict badge alone could never show, where
 * a check that flagged something and was then overruled by a confirm pass
 * simply vanished.
 *
 * Collapsed behind its own toggle: the badge and its findings stay the answer
 * for the common case, and this is what you open when the answer is not enough.
 *
 * Every "what happened next" line is DERIVED from the verdict sequence, its
 * metadata stamps and the checkpoint's gates — never from a record of the
 * decision itself, which the engine does not keep. The tooltip says so.
 */

const DERIVED_TITLE =
  'Derived by this panel from the verdicts, their metadata and the checkpoint’s gates — not a record the engine wrote.';

/** What an outcome pill means, where the word alone does not say it. */
const OUTCOME_TITLES: Partial<Record<ReviewStepOutcome, string>> = {
  UNCERTAIN: 'The judge did not pass this call and named no failing criterion.',
  UNREVIEWED: 'The reviewer could not give a verdict; the candidate was kept.',
};

interface ReviewStepsListProps {
  steps: ReviewStep[];
  /**
   * Controlled by the panel, because opening the list is what pays for the
   * checkpoint's gates: nothing is fetched until somebody actually asks.
   */
  open: boolean;
  onToggle: () => void;
  /** Whether the checkpoint's gates were known when the lines were derived. */
  gatesKnown?: boolean;
}

const ReviewStepRow: React.FC<{ step: ReviewStep }> = ({ step }) => {
  const [open, setOpen] = React.useState(false);
  const hasDetail =
    step.findings.length > 0 || Boolean(step.critique) || Boolean(step.adjustedPrompt);

  return (
    <li className="border border-white/10 bg-black/40" data-testid="review-step-row" data-tier={step.tier}>
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-1.5 px-1.5 py-1 text-left"
        aria-expanded={open}
        disabled={!hasDetail}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
          #{step.attempt}
        </span>
        <span className="text-[11px] text-zinc-200">{step.label}</span>
        <span
          data-testid="review-step-outcome"
          className={`shrink-0 border px-1 py-0.5 text-[9px] uppercase tracking-wide ${
            FINDING_STYLES[step.outcome] || FINDING_STYLES.UNCERTAIN
          }`}
          title={OUTCOME_TITLES[step.outcome]}
        >
          {step.outcome}
        </span>
        {step.model && (
          <span className="truncate font-mono text-[9px] text-zinc-500" title={step.model}>
            {step.model}
          </span>
        )}
        <span className="ml-auto font-mono text-[9px] text-zinc-500">
          {Number.isFinite(step.verdict.confidence) ? `conf ${step.verdict.confidence}` : ''}
          {step.verdict.severity ? ` · ${step.verdict.severity}` : ''}
        </span>
      </button>

      {step.next && (
        <p
          data-testid="review-step-next"
          className="border-t border-white/10 px-1.5 py-1 text-[10px] text-zinc-400"
          title={DERIVED_TITLE}
        >
          → {step.next}
        </p>
      )}

      {open && hasDetail && (
        <div
          className="space-y-1.5 border-t border-white/10 px-1.5 py-1.5"
          data-testid="review-step-detail"
        >
          <ReviewFindingsList findings={step.findings} />
          {step.critique && (
            <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-zinc-300">
              {step.critique}
            </p>
          )}
          {step.adjustedPrompt && (
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap border border-white/10 bg-black/70 p-2 font-mono text-[11px] text-slate-300">
              {step.adjustedPrompt}
            </pre>
          )}
        </div>
      )}
    </li>
  );
};

const ReviewStepsList: React.FC<ReviewStepsListProps> = ({
  steps,
  open,
  onToggle,
  gatesKnown,
}) => {
  if (steps.length === 0) return null;

  return (
    <div className="space-y-1 border-t border-white/10 pt-2" data-testid="review-steps">
      <button
        type="button"
        data-testid="review-steps-toggle"
        aria-expanded={open}
        className="text-[10px] uppercase tracking-wide text-zinc-500 hover:text-zinc-300"
        onClick={onToggle}
      >
        Review steps ({steps.length})
      </button>

      {open && (
        <>
          <ul className="space-y-1" data-testid="review-steps-list">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                {/* Verdicts are append-only ACROSS re-executions, and a user
                    regenerate restarts at attempt 1. Without this line the two
                    cascades read as one impossible sequence. */}
                {index > 0 && step.cascade !== steps[index - 1].cascade && (
                  <li
                    data-testid="review-steps-cascade-separator"
                    className="flex items-center gap-2 pt-1 text-[9px] uppercase tracking-wider text-zinc-600"
                  >
                    <span className="h-px flex-1 bg-white/10" aria-hidden />
                    run {step.cascade}
                    <span className="h-px flex-1 bg-white/10" aria-hidden />
                  </li>
                )}
                <ReviewStepRow step={step} />
              </React.Fragment>
            ))}
          </ul>
          {!gatesKnown && (
            <p className="text-[9px] text-zinc-600" data-testid="review-steps-no-gates">
              The checkpoint’s gates are not known here, so a check that stopped is only reported as
              “not escalated”.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewStepsList;
